import Decimal from 'decimal.js';
import type { CronosPriceHint, LedgerClassification, LedgerEvent, OwnedWallet, TokenRegistryEntry } from './types';
import type { FiCsvDiagnosticRow } from './fiDiagnostics';
import { nativeEthEurPerUnit } from './cryptoPrices';
import { convertUsdToEur } from './eurFx';
import { applyCronosWalletTouchingFilter } from './cronosWalletTouchingFilter';
import { ownedAddressSetFromWallets } from './walletLotContinuity';
import { appendArbitrumFiDiagnostic } from './fiDiagnostics';
import { fiMetaForArbitrumInternalRow } from './fiCronosLedger';
import { isCronosIncomePayoutContract, mergeIncomePayerContracts } from './cronosIncomeContracts';
import { applyCexDepositCounterpartyClassification } from './cexDepositCounterparty';
import { labelForAddress } from './ownedWallets';
import {
  applyCronosHistoricPrices,
  classifyTokentxMovement,
  inTimeWindow,
  mergeReviewFlags,
} from './cronosImport';
import type { ApplyCronosPricesProgress } from './cronosImport';
import {
  buildAggregatesByTxHash,
  buildTokenRegistryFromTokentxRows,
  displayTokenSymbolFromExplorerRow,
  normalizeContractAddress,
  normalizeTokenDecimalsFromExplorerRow,
  summarizeAggregate,
  swapReviewFlagsForAggregate,
  txAppearsSuccessfulFromTxlistRows,
  txHashFromExplorerRow,
} from './tokenRegistry';

/** Canonical WETH on Arbitrum One — priced as ETH (same economic unit). */
export const ARBITRUM_WETH_CONTRACT_LOWER = '0x82af49447d8a07e3bd95bd0d56f35241523fbab1';

/** Arbitrum One — Etherscan API V2 `chainid` (replaces per-explorer v1 hosts like api.arbiscan.io/api). */
export const ARBITRUM_ONE_CHAIN_ID = 42161;

const STABLE_USD_SYMBOLS = new Set(['USDC', 'USDT', 'DAI', 'USDC.E']);

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function fromRaw(value: string, decimals: number): Decimal {
  try {
    return new Decimal(value || '0').div(new Decimal(10).pow(decimals));
  } catch {
    return new Decimal(0);
  }
}

/** Must end with `/v2/api`. Plain `https://api.etherscan.io/api` (no `v2`) still hits deprecated V1 and returns NOTOK. */
const ETHERSCAN_MULTICHAIN_V2_API_BASE = 'https://api.etherscan.io/v2/api';

/**
 * Etherscan API V2 base (`chainid` required per request). Dev: `setupProxy` maps `/__arbiscan_api__` → `.../v2/api`.
 * @see https://docs.etherscan.io/v2-migration
 */
function normalizeEtherscanV2ApiBase(raw: string | undefined): string | undefined {
  if (!raw?.trim()) return undefined;
  const b = raw.trim().replace(/\/$/, '');
  if (/\/v2\/api$/i.test(b)) return b;
  // Legacy per-explorer hosts — always use unified multichain V2 URL + chainid.
  if (/arbiscan\.io/i.test(b)) return ETHERSCAN_MULTICHAIN_V2_API_BASE;
  // Very common mistake: ...etherscan.io/api (no v2) — still deprecated even with chainid in query.
  if (/^https?:\/\/api\.etherscan\.io\/api$/i.test(b)) return ETHERSCAN_MULTICHAIN_V2_API_BASE;
  // Host only — still not a valid V2 entrypoint.
  if (/^https?:\/\/api\.etherscan\.io\/?$/i.test(b)) return ETHERSCAN_MULTICHAIN_V2_API_BASE;
  return b;
}

function getEtherscanV2ApiBase(): string {
  const fromEnv = normalizeEtherscanV2ApiBase(process.env.REACT_APP_ARBISCAN_API_BASE);
  if (fromEnv) return fromEnv;
  if (process.env.NODE_ENV === 'development') {
    return '/__arbiscan_api__';
  }
  return ETHERSCAN_MULTICHAIN_V2_API_BASE;
}

function isEtherscanApiSuccessStatus(status: string | undefined): boolean {
  const s = String(status ?? '').toLowerCase();
  return s === '1' || s === '200' || s === 'success';
}

async function fetchArbiscanResult(params: string, apiKey?: string): Promise<unknown[]> {
  const base = getEtherscanV2ApiBase();
  const url = `${base}?chainid=${ARBITRUM_ONE_CHAIN_ID}&${params}${
    apiKey ? `&apikey=${encodeURIComponent(apiKey)}` : ''
  }`;

  let lastErr = '';
  for (let attempt = 0; attempt < 12; attempt++) {
    const res = await fetch(url);
    const text = await res.text();

    if (res.status === 429) {
      const ra = res.headers.get('Retry-After');
      let waitMs = ra
        ? Math.min(parseInt(ra, 10) * 1000, 120000)
        : Math.min(600 * 2 ** attempt, 60000);
      if (!Number.isFinite(waitMs) || waitMs < 400) waitMs = 600 * 2 ** attempt;
      await sleep(waitMs);
      lastErr = 'HTTP 429 Too Many Requests from Etherscan API V2';
      continue;
    }

    let data: { result?: unknown; status?: string; message?: string };
    try {
      data = JSON.parse(text) as { result?: unknown; status?: string; message?: string };
    } catch {
      const hint = text.trimStart().startsWith('<')
        ? ' (got HTML — check dev proxy / __arbiscan_api__ → api.etherscan.io/v2/api in setupProxy.js)'
        : '';
      throw new Error(
        `Arbitrum (Etherscan API V2) response is not JSON${hint}. HTTP ${res.status}. ${text.slice(0, 160).replace(/\s+/g, ' ')}${text.length > 160 ? '…' : ''}`
      );
    }

    const msg = String(data.message ?? '');
    const r = data.result;
    if (data.status === '0' && (msg === 'No transactions found' || msg.includes('No records found'))) {
      return [];
    }
    if (typeof r === 'string' && r.startsWith('Max rate limit')) {
      await sleep(Math.min(600 * 2 ** attempt, 60000));
      lastErr = r;
      continue;
    }
    if (!isEtherscanApiSuccessStatus(data.status)) {
      const detail =
        typeof r === 'string' && r.trim()
          ? r
          : msg && msg !== 'NOTOK'
            ? msg
            : text.slice(0, 280);
      throw new Error(`Arbitrum (Etherscan API V2): ${detail}`);
    }
    if (Array.isArray(r)) return r;
    if (r && typeof r === 'object') return [r];
    return [];
  }
  throw new Error(
    lastErr ||
      'Arbitrum (Etherscan API V2): too many rate-limit retries — wait a minute and try again, or reduce history size.'
  );
}

async function fetchAllTokentxArb(
  address: string,
  apiKey?: string,
  pageDelayMs = 450
): Promise<Record<string, unknown>[]> {
  const out: Record<string, unknown>[] = [];
  let page = 1;
  while (true) {
    if (page > 1 && pageDelayMs > 0) await sleep(pageDelayMs);
    const batch = (await fetchArbiscanResult(
      `module=account&action=tokentx&address=${encodeURIComponent(address)}&page=${page}&offset=1000&sort=asc`,
      apiKey
    )) as Record<string, unknown>[];
    if (batch.length === 0) break;
    out.push(...batch);
    if (batch.length < 1000) break;
    page++;
  }
  return out;
}

async function fetchAllTxlistArb(
  address: string,
  apiKey?: string,
  pageDelayMs = 450
): Promise<Record<string, unknown>[]> {
  const out: Record<string, unknown>[] = [];
  let page = 1;
  while (true) {
    if (page > 1 && pageDelayMs > 0) await sleep(pageDelayMs);
    const batch = (await fetchArbiscanResult(
      `module=account&action=txlist&address=${encodeURIComponent(address)}&page=${page}&offset=1000&sort=asc`,
      apiKey
    )) as Record<string, unknown>[];
    if (batch.length === 0) break;
    out.push(...batch);
    if (batch.length < 1000) break;
    page++;
  }
  return out;
}

async function fetchAllInternalArb(
  address: string,
  apiKey?: string,
  pageDelayMs = 450
): Promise<Record<string, unknown>[]> {
  const out: Record<string, unknown>[] = [];
  let page = 1;
  while (true) {
    if (page > 1 && pageDelayMs > 0) await sleep(pageDelayMs);
    const batch = (await fetchArbiscanResult(
      `module=account&action=txlistinternal&address=${encodeURIComponent(address)}&page=${page}&offset=1000&sort=asc`,
      apiKey
    )) as Record<string, unknown>[];
    if (batch.length === 0) break;
    out.push(...batch);
    if (batch.length < 1000) break;
    page++;
  }
  return out;
}

function tokentxDedupeKey(row: Record<string, unknown>): string {
  const hash = String(row.hash ?? row.transactionHash ?? '').toLowerCase();
  const li = row.logIndex ?? row.log_index ?? '';
  const c = String(row.contractAddress || row.contract_address || '').toLowerCase();
  return `t:${hash}:${li}:${c}`;
}

function nativeTxDedupeKey(row: Record<string, unknown>): string {
  const hash = String(row.hash ?? '').toLowerCase();
  const from = String(row.from || '').toLowerCase();
  const to = String(row.to || '').toLowerCase();
  const val = String(row.value ?? '0');
  return `n:${hash}:${from}:${to}:${val}`;
}

export function hintForArbitrumTokentx(row: Record<string, unknown>): CronosPriceHint {
  const contract = String(row.contractAddress || row.contract_address || '').toLowerCase();
  const sym = String(row.tokenSymbol || row.token_symbol || '?').toUpperCase();
  const blockNum = Number(row.blockNumber ?? row.block_number ?? 0);
  if (contract === ARBITRUM_WETH_CONTRACT_LOWER || sym === 'WETH') {
    return {
      mode: 'native_eth',
      contractAddress: '',
      tokenSymbol: 'ETH',
      blockNumber: Number.isFinite(blockNum) ? blockNum : 0,
    };
  }
  return {
    mode: 'crc20',
    contractAddress: contract,
    tokenSymbol: sym,
    blockNumber: Number.isFinite(blockNum) ? blockNum : 0,
  };
}

export function hintForArbitrumNative(row: Record<string, unknown>): CronosPriceHint {
  const blockNum = Number(row.blockNumber ?? row.block_number ?? 0);
  return {
    mode: 'native_eth',
    contractAddress: '',
    tokenSymbol: 'ETH',
    blockNumber: Number.isFinite(blockNum) ? blockNum : 0,
  };
}

export type ArbitrumImportProgress = (msg: string) => void;

export type BuildArbitrumUnpricedOptions = {
  wallets: OwnedWallet[];
  apiKey?: string;
  onProgress?: ArbitrumImportProgress;
  explorerPageDelayMs?: number;
  timeFromMs?: number;
  timeToMs?: number;
  incomePayerAddressLines?: string[];
  cexDepositWallets?: OwnedWallet[];
};

export type ArbitrumUnpricedFetchMeta = {
  walletCount: number;
  internalTransferCount: number;
  timeFilteredOut: number;
  dedupedTokenTransfers: number;
  dedupedNativeTx: number;
  dedupedInternalTx: number;
  tokenRegistrySize: number;
  revertedOrFailedTxRowsSkipped: number;
  cexDepositReclassified?: number;
};

export type ArbitrumRawExplorerData = {
  tokenTransfers: Record<string, unknown>[];
  nativeTransactions: Record<string, unknown>[];
  internalTransactions: Record<string, unknown>[];
};

export async function buildArbitrumLedgerEventsUnpriced(
  options: BuildArbitrumUnpricedOptions
): Promise<{
  events: LedgerEvent[];
  warnings: string[];
  meta: ArbitrumUnpricedFetchMeta;
  rawExplorer: ArbitrumRawExplorerData;
  tokenRegistry: TokenRegistryEntry[];
  diagnostics: FiCsvDiagnosticRow[];
}> {
  const warnings: string[] = [];
  let events: LedgerEvent[] = [];
  const diagnostics: FiCsvDiagnosticRow[] = [];
  const explorerPageDelay = options.explorerPageDelayMs ?? 450;
  const prog = options.onProgress;
  const apiKey =
    options.apiKey?.trim() ||
    process.env.REACT_APP_ARBISCAN_API_KEY?.trim() ||
    process.env.REACT_APP_ETHERSCAN_API_KEY?.trim();
  const wallets = options.wallets;
  const timeFromMs = options.timeFromMs;
  const timeToMs = options.timeToMs;

  const emptyMeta: ArbitrumUnpricedFetchMeta = {
    walletCount: 0,
    internalTransferCount: 0,
    timeFilteredOut: 0,
    dedupedTokenTransfers: 0,
    dedupedNativeTx: 0,
    dedupedInternalTx: 0,
    tokenRegistrySize: 0,
    revertedOrFailedTxRowsSkipped: 0,
  };

  if (!wallets.length) {
    return {
      events: [],
      warnings: ['No owned wallets configured.'],
      meta: emptyMeta,
      rawExplorer: { tokenTransfers: [], nativeTransactions: [], internalTransactions: [] },
      tokenRegistry: [],
      diagnostics: [],
    };
  }

  if (!apiKey?.trim()) {
    return {
      events: [],
      warnings: [
        'Etherscan API V2 key required (same key for Arbitrum via chainid) — create one at etherscan.io/apidashboard, then set REACT_APP_ARBISCAN_API_KEY or REACT_APP_ETHERSCAN_API_KEY, or pass apiKey.',
      ],
      meta: emptyMeta,
      rawExplorer: { tokenTransfers: [], nativeTransactions: [], internalTransactions: [] },
      tokenRegistry: [],
      diagnostics: [],
    };
  }

  const ownedSet = new Set(wallets.map((w) => w.address.toLowerCase()));
  const incomePayers = mergeIncomePayerContracts(options.incomePayerAddressLines);
  const tokenDedupe = new Map<string, Record<string, unknown>>();
  const nativeDedupe = new Map<string, Record<string, unknown>>();
  const internalDedupe = new Map<string, Record<string, unknown>>();
  let timeFilteredOut = 0;
  let revertedOrFailedTxRowsSkipped = 0;

  prog?.(`Arbitrum: token transfers for ${wallets.length} address(es)…`);
  for (const w of wallets) {
    prog?.(`— tokentx: ${w.label} (${w.address.slice(0, 8)}…)…`);
    const tokenRows = await fetchAllTokentxArb(w.address, apiKey, explorerPageDelay);
    for (const row of tokenRows) {
      const k = tokentxDedupeKey(row);
      if (!tokenDedupe.has(k)) tokenDedupe.set(k, row);
    }
  }
  prog?.(`tokentx unique rows: ${tokenDedupe.size}`);

  const tokenTransfers = Array.from(tokenDedupe.values()) as Record<string, unknown>[];
  const registryMap = buildTokenRegistryFromTokentxRows(tokenTransfers, ownedSet);
  const tokenRegistry: TokenRegistryEntry[] = Array.from(registryMap.values()).sort((a, b) =>
    a.contractAddress.localeCompare(b.contractAddress)
  );
  prog?.(`Token registry: ${tokenRegistry.length} contract(s).`);

  prog?.(`Normal transactions (txlist) for ${wallets.length} wallet(s)…`);
  for (const w of wallets) {
    const txs = await fetchAllTxlistArb(w.address, apiKey, explorerPageDelay);
    for (const row of txs) {
      const k = nativeTxDedupeKey(row);
      if (!nativeDedupe.has(k)) nativeDedupe.set(k, row);
    }
  }
  prog?.(`txlist unique rows: ${nativeDedupe.size}`);

  prog?.(`Internal transactions for ${wallets.length} wallet(s)…`);
  for (const w of wallets) {
    const internalRows = await fetchAllInternalArb(w.address, apiKey, explorerPageDelay);
    for (const row of internalRows) {
      const hash = String(row.hash ?? '').toLowerCase();
      const from = String(row.from || '').toLowerCase();
      const k = `i:${hash}:${from}:${String(row.to || '').toLowerCase()}`;
      if (!internalDedupe.has(k)) internalDedupe.set(k, row);
    }
  }
  const internalTransactions = Array.from(internalDedupe.values()) as Record<string, unknown>[];
  const nativeTransactions = Array.from(nativeDedupe.values()) as Record<string, unknown>[];

  const aggregates = buildAggregatesByTxHash(
    tokenTransfers,
    nativeTransactions,
    internalTransactions
  );

  for (const row of tokenTransfers) {
    const from = String(row.from || '').toLowerCase();
    const to = String(row.to || '').toLowerCase();
    const contractAddr = normalizeContractAddress(
      String(row.contractAddress || row.contract_address || '')
    );
    let sym = displayTokenSymbolFromExplorerRow(row, contractAddr);
    const dec = normalizeTokenDecimalsFromExplorerRow(row);
    const raw = String(row.value ?? '0');
    const tsSec = Number(row.timeStamp ?? row.timestamp ?? 0);
    const ts = tsSec * 1000;
    const hash = String(row.hash ?? row.transactionHash ?? '');
    const hashKey = txHashFromExplorerRow(row);
    const amt = fromRaw(raw, dec);
    if (amt.lte(0)) continue;

    if (!inTimeWindow(ts, timeFromMs, timeToMs)) {
      timeFilteredOut++;
      continue;
    }

    const aggFail = aggregates.get(hashKey);
    if (aggFail && txAppearsSuccessfulFromTxlistRows(aggFail.txlist) === false) {
      revertedOrFailedTxRowsSkipped++;
      continue;
    }

    const meta = classifyTokentxMovement({ from, to, ownedSet, wallets });
    let kind: LedgerEvent['kind'] = meta.kind;
    let classification: LedgerClassification = meta.classification;
    let incomeTag: Partial<LedgerEvent> = {};
    if (
      meta.kind === 'acquisition' &&
      meta.classification === 'TRANSFER_IN' &&
      isCronosIncomePayoutContract(from, incomePayers)
    ) {
      kind = 'income';
      classification = 'INCOME';
      incomeTag = {
        fiTaxCategory: 'TAXABLE_INCOME',
        fiSubreason: 'ARBITRUM_ALLOWLIST_STAKING_OR_REWARD',
        explanation:
          'Incoming ERC-20 from a configured income/payer address — taxable income at FMV when priced (FIFO basis = EUR).',
      };
    }
    if (meta.classification === 'UNKNOWN_REVIEW') {
      warnings.push(`Review: ERC-20 ${sym} tx ${hash.slice(0, 10)}… — ${meta.explanation}`);
    }

    const agg = aggregates.get(hashKey);
    const summ = agg
      ? summarizeAggregate(agg)
      : {
          sourceRecordCount: 0,
          sourceSummary: 'none',
          hasTransactionRecord: false,
          hasTokenTransferRecord: false,
          hasInternalTxRecord: false,
        };
    const swapFlags = agg ? swapReviewFlagsForAggregate(agg, ownedSet) : [];
    const interpretationBasis: import('./types').InterpretationBasis = summ.hasTransactionRecord
      ? 'token_transfer_with_tx_context'
      : 'token_transfer_primary';

    let assetSym = sym;
    if (contractAddr === ARBITRUM_WETH_CONTRACT_LOWER || sym.toUpperCase() === 'WETH') {
      assetSym = 'ETH';
    }

    const base: LedgerEvent = {
      ts,
      source: 'arbitrum',
      kind,
      asset: assetSym,
      amount: amt,
      eurValue: null,
      ref: hash,
      classification,
      confidence: meta.confidence,
      reviewFlags: mergeReviewFlags(meta.reviewFlags, swapFlags),
      explanation: incomeTag.explanation ?? meta.explanation,
      fromAddress: from,
      toAddress: to,
      primaryWalletAddress: meta.primary,
      primaryWalletLabel: labelForAddress(wallets, meta.primary),
      tokenContractAddress: contractAddr || undefined,
      discoveredFromWalletFetch: true,
      tokenRegistryMatch: contractAddr ? registryMap.has(contractAddr) : false,
      contractBackfillUsed: false,
      interpretationBasis,
      hasTransactionRecord: summ.hasTransactionRecord,
      hasTokenTransferRecord: summ.hasTokenTransferRecord,
      hasInternalTxRecord: summ.hasInternalTxRecord,
      sourceRecordCount: summ.sourceRecordCount,
      sourceSummary: summ.sourceSummary,
      ...(kind === 'internal_transfer' ? fiMetaForArbitrumInternalRow(classification) : {}),
      ...incomeTag,
    };

    if (kind === 'internal_transfer') {
      events.push(base);
      appendArbitrumFiDiagnostic(diagnostics, base);
    } else if (classification === 'UNKNOWN_REVIEW') {
      events.push(base);
      appendArbitrumFiDiagnostic(diagnostics, base);
    } else {
      events.push({
        ...base,
        cronosPriceHint: hintForArbitrumTokentx(row),
      });
    }
  }

  for (const row of nativeTransactions) {
    const from = String(row.from || '').toLowerCase();
    const to = String(row.to || '').toLowerCase();
    const rawVal = String(row.value ?? '0');
    const tsSec = Number(row.timeStamp ?? 0);
    const ts = tsSec * 1000;
    const hash = String(row.hash ?? '');
    const hashKey = txHashFromExplorerRow(row);
    const amt = fromRaw(rawVal, 18);
    if (amt.lte(0)) continue;

    if (!inTimeWindow(ts, timeFromMs, timeToMs)) {
      timeFilteredOut++;
      continue;
    }

    if (txAppearsSuccessfulFromTxlistRows([row]) === false) {
      revertedOrFailedTxRowsSkipped++;
      continue;
    }

    const meta = classifyTokentxMovement({ from, to, ownedSet, wallets });
    let kindN: LedgerEvent['kind'] = meta.kind;
    let classificationN: LedgerClassification = meta.classification;
    let incomeTagN: Partial<LedgerEvent> = {};
    if (
      meta.kind === 'acquisition' &&
      meta.classification === 'TRANSFER_IN' &&
      isCronosIncomePayoutContract(from, incomePayers)
    ) {
      kindN = 'income';
      classificationN = 'INCOME';
      incomeTagN = {
        fiTaxCategory: 'TAXABLE_INCOME',
        fiSubreason: 'ARBITRUM_ALLOWLIST_STAKING_OR_REWARD',
        explanation:
          'Incoming native ETH from a configured income/payer address — taxable income at FMV when priced.',
      };
    }
    if (meta.classification === 'UNKNOWN_REVIEW') {
      warnings.push(`Review: native ETH tx ${hash.slice(0, 10)}… — ${meta.explanation}`);
    }

    const agg = aggregates.get(hashKey);
    const summ = agg
      ? summarizeAggregate(agg)
      : {
          sourceRecordCount: 0,
          sourceSummary: 'none',
          hasTransactionRecord: false,
          hasTokenTransferRecord: false,
          hasInternalTxRecord: false,
        };
    const swapFlags = agg ? swapReviewFlagsForAggregate(agg, ownedSet) : [];

    const interpretationBasis: import('./types').InterpretationBasis =
      summ.hasTokenTransferRecord && !summ.hasTransactionRecord
        ? 'merged_evidence_weak'
        : 'native_transaction_primary';

    const base: LedgerEvent = {
      ts,
      source: 'arbitrum',
      kind: kindN,
      asset: 'ETH',
      amount: amt,
      eurValue: null,
      ref: hash,
      classification: classificationN,
      confidence: meta.confidence,
      reviewFlags: mergeReviewFlags(meta.reviewFlags, swapFlags),
      explanation: incomeTagN.explanation ?? meta.explanation,
      fromAddress: from,
      toAddress: to,
      primaryWalletAddress: meta.primary,
      primaryWalletLabel: labelForAddress(wallets, meta.primary),
      discoveredFromWalletFetch: true,
      tokenRegistryMatch: false,
      contractBackfillUsed: false,
      interpretationBasis,
      hasTransactionRecord: summ.hasTransactionRecord,
      hasTokenTransferRecord: summ.hasTokenTransferRecord,
      hasInternalTxRecord: summ.hasInternalTxRecord,
      sourceRecordCount: summ.sourceRecordCount,
      sourceSummary: summ.sourceSummary,
      ...(kindN === 'internal_transfer' ? fiMetaForArbitrumInternalRow(classificationN) : {}),
      ...incomeTagN,
    };

    if (kindN === 'internal_transfer') {
      events.push(base);
      appendArbitrumFiDiagnostic(diagnostics, base);
    } else if (classificationN === 'UNKNOWN_REVIEW') {
      events.push(base);
      appendArbitrumFiDiagnostic(diagnostics, base);
    } else {
      events.push({
        ...base,
        cronosPriceHint: hintForArbitrumNative(row),
      });
    }
  }

  events.sort((a, b) => a.ts - b.ts);

  let cexDepositReclassified = 0;
  if (options.cexDepositWallets?.length) {
    const cexSet = new Set(options.cexDepositWallets.map((w) => w.address.toLowerCase()));
    const applied = applyCexDepositCounterpartyClassification(events, {
      ownedAddresses: ownedSet,
      cexDepositAddresses: cexSet,
    });
    events = applied.events;
    cexDepositReclassified = applied.reclassified;
  }

  const internalTransferCount = events.filter((e) => e.kind === 'internal_transfer').length;

  return {
    events,
    warnings,
    meta: {
      walletCount: wallets.length,
      internalTransferCount,
      timeFilteredOut,
      dedupedTokenTransfers: tokenDedupe.size,
      dedupedNativeTx: nativeDedupe.size,
      dedupedInternalTx: internalDedupe.size,
      tokenRegistrySize: tokenRegistry.length,
      revertedOrFailedTxRowsSkipped,
      cexDepositReclassified,
    },
    rawExplorer: { tokenTransfers, nativeTransactions, internalTransactions },
    tokenRegistry,
    diagnostics,
  };
}

export type ApplyArbitrumPricesProgress = (msg: string) => void;

/**
 * EUR for Arbitrum rows: native ETH / WETH via bundled proxy ETH/USD; USD stables via Frankfurter;
 * other ERC-20 → 0 EUR + review flags (no swap-derivation pass yet).
 */
export async function applyArbitrumHistoricPrices(
  events: LedgerEvent[],
  options?: {
    onProgress?: ApplyArbitrumPricesProgress;
    ownedWallets?: OwnedWallet[];
  }
): Promise<{ events: LedgerEvent[]; pricedCount: number; stillMissing: string[] }> {
  const prog = options?.onProgress;
  const stillMissing: string[] = [];
  let pricedCount = 0;
  const working: LedgerEvent[] = events.map((e) => ({ ...e }));

  if (options?.ownedWallets?.length) {
    applyCronosWalletTouchingFilter(working, ownedAddressSetFromWallets(options.ownedWallets));
  }

  for (const ev of working) {
    if (ev.source !== 'arbitrum') continue;
    if (ev.kind === 'internal_transfer') continue;
    if (ev.classification === 'IGNORE_NON_TAX') continue;
    if (ev.eurValue != null) continue;

    const h = ev.cronosPriceHint;
    if (!h) continue;

    const date = new Date(ev.ts);
    const refShort = ev.ref ? `${ev.ref.slice(0, 10)}…` : '—';

    if (h.mode === 'native_eth') {
      try {
        const unit = await nativeEthEurPerUnit(date, h.blockNumber);
        if (unit != null) {
          ev.eurValue = unit.mul(ev.amount);
          ev.cronosPriceHint = undefined;
          pricedCount++;
          const prev = ev.reviewFlags ?? [];
          ev.reviewFlags = Array.from(
            new Set([...prev, 'arbitrum_eth_priced', 'eth_usd_proxy_from_btc_daily'])
          );
        } else {
          const iso = date.toISOString().slice(0, 10);
          stillMissing.push(
            `${iso} | ${ev.kind} | ${ev.asset} | ETH/EUR unavailable (bundled series) | ref ${refShort}`
          );
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        stillMissing.push(
          `${date.toISOString().slice(0, 10)} | ${ev.kind} | ${ev.asset} | ${msg} | ref ${refShort}`
        );
      }
      continue;
    }

    if (h.mode === 'crc20') {
      const sym = ev.asset.toUpperCase();
      if (sym === 'ETH' || sym === 'WETH' || ev.tokenContractAddress?.toLowerCase() === ARBITRUM_WETH_CONTRACT_LOWER) {
        try {
          const unit = await nativeEthEurPerUnit(date, h.blockNumber);
          if (unit != null) {
            ev.eurValue = unit.mul(ev.amount);
            ev.cronosPriceHint = undefined;
            pricedCount++;
            const prev = ev.reviewFlags ?? [];
            ev.reviewFlags = Array.from(
              new Set([...prev, 'arbitrum_weth_as_eth', 'eth_usd_proxy_from_btc_daily'])
            );
          }
        } catch {
          /* fall through */
        }
        continue;
      }

      if (STABLE_USD_SYMBOLS.has(sym)) {
        try {
          ev.eurValue = await convertUsdToEur(ev.amount, date);
          ev.cronosPriceHint = undefined;
          pricedCount++;
          const prev = ev.reviewFlags ?? [];
          ev.reviewFlags = Array.from(new Set([...prev, 'arbitrum_stable_usd_parity']));
        } catch {
          stillMissing.push(
            `${date.toISOString().slice(0, 10)} | ${ev.kind} | ${sym} | Frankfurter EUR failed | ref ${refShort}`
          );
        }
        continue;
      }

      ev.eurValue = new Decimal(0);
      ev.cronosPriceHint = undefined;
      ev.valuationFlag = 'missing_proceeds_review';
      const prev = ev.reviewFlags ?? [];
      ev.reviewFlags = Array.from(
        new Set([...prev, 'arbitrum_token_unpriced', 'missing_cost_basis_pending_review'])
      );
      const extra =
        'Arbitrum: ERC-20 token not priced automatically — 0 EUR pending review (swap-derivation not enabled for Arbitrum yet).';
      ev.explanation = ev.explanation ? `${ev.explanation} ${extra}` : extra;
    }
  }

  prog?.(
    `Arbitrum pricing: ${pricedCount} row(s) with EUR; ${stillMissing.length} anchor/FX failure(s); unpriced ERC-20 → 0 EUR with review flags.`
  );

  return { events: working, pricedCount, stillMissing };
}

/**
 * Run Arbitrum EUR pass first, then full Cronos pricing (CRO/swap pipeline).
 * Safe when no Arbitrum rows exist. Merges `stillMissing` from both passes.
 */
export async function applyExplorerHistoricPrices(
  events: LedgerEvent[],
  options?: {
    coingeckoDelayMs?: number;
    onProgress?: ApplyCronosPricesProgress;
    ownedWallets?: OwnedWallet[];
  }
): Promise<Awaited<ReturnType<typeof applyCronosHistoricPrices>>> {
  const arb = await applyArbitrumHistoricPrices(events, {
    onProgress: options?.onProgress,
    ownedWallets: options?.ownedWallets,
  });
  const cro = await applyCronosHistoricPrices(arb.events, options);
  return {
    ...cro,
    stillMissing: [...arb.stillMissing, ...cro.stillMissing],
  };
}
